import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as React from "react";

import { cn } from "../../lib/utils";
import { Input } from "./input";
import { PopoverContent } from "./popover";

type Option = { value: string; label: string };

interface EditableSelectProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "onSelect"> {
	options: Option[];
	onChange?: React.ChangeEventHandler<HTMLInputElement>;
	onSelect?: (val: string) => void;
	renderOption?: (option: Option) => React.ReactNode;
}

const EditableSelect = React.forwardRef<HTMLInputElement, EditableSelectProps>(
	(
		{ options, renderOption, className, onFocus, onBlur, onChange, onSelect, value, ...props },
		forwardedRef,
	) => {
		const [open, setOpen] = React.useState(false);
		const [inputValue, setInputValue] = React.useState((value as string) ?? "");
		const inputRef = React.useRef<HTMLInputElement>(null);

		const mergedRef = React.useCallback(
			(node: HTMLInputElement | null) => {
				(inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
				if (typeof forwardedRef === "function") forwardedRef(node);
				else if (forwardedRef) forwardedRef.current = node;
			},
			[forwardedRef],
		);

		React.useEffect(() => {
			if (value !== undefined) setInputValue(value as string);
		}, [value]);

		const filteredOptions = React.useMemo(() => {
			if (!inputValue) return options;
			return options.filter((opt) =>
				opt.label.toLowerCase().includes(inputValue.toLowerCase()),
			);
		}, [inputValue, options]);

		const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			setInputValue(e.target.value);
			setOpen(true);
			onChange?.(e);
		};

		const handleOptionSelect = (option: Option) => {
			setInputValue(option.value);
			onChange?.({ target: { value: option.value } } as React.ChangeEvent<HTMLInputElement>);
			onSelect?.(option.value);
			setOpen(false);
		};

		return (
			<PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
				<PopoverPrimitive.Anchor asChild>
					<Input
						ref={mergedRef}
						value={inputValue}
						onChange={handleInputChange}
						onFocus={(e) => {
							setOpen(true);
							onFocus?.(e);
						}}
						onBlur={(e) => {
							onBlur?.(e);
						}}
						className={className}
						{...props}
					/>
				</PopoverPrimitive.Anchor>

				<PopoverContent
					align="start"
					sideOffset={4}
					onOpenAutoFocus={(e) => e.preventDefault()}
					onFocusOutside={(e) => {
						if (e.target === inputRef.current) e.preventDefault();
					}}
					onInteractOutside={(e) => {
						if (
							e.target === inputRef.current ||
							inputRef.current?.contains(e.target as Node)
						) {
							e.preventDefault();
						}
					}}
					className={cn(
						"w-[var(--radix-popover-trigger-width)] p-1",
						filteredOptions.length === 0 && "hidden",
					)}
				>
					{filteredOptions.map((option) => (
						<div
							key={option.value}
							className="cursor-pointer rounded-sm px-2 py-1.5 text-sm hover:bg-muted"
							onPointerDown={(e) => e.preventDefault()}
							onClick={() => handleOptionSelect(option)}
						>
							{renderOption ? renderOption(option) : option.label}
						</div>
					))}
				</PopoverContent>
			</PopoverPrimitive.Root>
		);
	},
);

EditableSelect.displayName = "EditableSelect";

export { EditableSelect };
export type { Option as EditableSelectOption, EditableSelectProps };
